import React from 'react';
import { ProFormDatePicker } from '@ant-design/pro-form';
import dayjs from 'dayjs';
import styles from '@/components/Kanban/styles.less';
import { useIntl } from 'umi';
import { getAntdLocale } from '@/utils/locale';

export type ProFormDatePickerWithOptionalTimeProps = {
  name: string;
  label?: React.ReactNode;
  rules?: any[];
  initialWithTime?: boolean;
  fieldProps?: any;
  style?: React.CSSProperties;
};

const ProFormDatePickerWithOptionalTime: React.FC<ProFormDatePickerWithOptionalTimeProps> = ({
  name,
  label,
  rules,
  initialWithTime = false,
  fieldProps,
  style,
}) => {
  const intl = useIntl();
  const antdLocale = getAntdLocale(intl.locale);
  const [withTime, setWithTime] = React.useState<boolean>(initialWithTime);

  return (
    <ProFormDatePicker
      name={name}
      showTime={withTime ? ({
        format: 'HH:mm',
        minuteStep: 15,
        hideDisabledOptions: true,
        disabledTime: (current) => {
          const now = dayjs();
          const value = current ? dayjs(current) : null;
          const isToday = value && value.isSame(now, 'day');
          if (!isToday) return {};
          const disabledHours = () => Array.from({ length: now.hour() }, (_, i) => i);
          const disabledMinutes = (selectedHour: number) => {
            if (selectedHour > now.hour()) return [];
            if (selectedHour < now.hour()) return Array.from({ length: 60 }, (_, i) => i);
            return Array.from({ length: now.minute() }, (_, i) => i);
          };
          return { disabledHours, disabledMinutes } as any;
        },
      }) : undefined}
      style={{ width: '100%', ...(style || {}) }}
      fieldProps={{
        style: { width: '100%', ...(fieldProps?.style || {}) },
        locale: antdLocale.DatePicker,
        showNow: false,
        popupClassName: styles.normalDatePicker,
        format: 'YYYY-MM-DD' + (withTime ? ' HH:mm' : ''),
        disabledDate: (current) => current && current < dayjs().startOf('day'),
        renderExtraFooter: () => (
          <div style={{ textAlign: 'right' }}>
            <a
              onClick={(e) => {
                e.preventDefault();
                setWithTime((v) => !v);
              }}
            >
              {withTime
                ? intl.formatMessage({ id: 'pages.tasks.removeTime', defaultMessage: 'Remove time' })
                : intl.formatMessage({ id: 'pages.tasks.addTime', defaultMessage: 'Add time' })}
            </a>
          </div>
        ),
        ...(fieldProps || {}),
      }}
      label={label}
      rules={rules}
    />
  );
};

export default ProFormDatePickerWithOptionalTime;
